/**
 * permissions.js — thin helpers for the runtime permission prompts
 * GreenCharge actually needs (camera for QR scanning, location for the
 * charger map, push notifications for battery-full alerts). Each helper
 * is fail-safe: if the underlying plugin is unavailable the call
 * resolves with `state: 'unavailable'` so a UI component can still
 * degrade gracefully rather than hang on a promise.
 *
 * Returned shape:
 *   { state: 'granted' | 'denied' | 'prompt' | 'restricted' | 'unavailable' }
 */
import { isNative } from './platform.js';

async function safe(fn) {
  try { return await fn(); } catch { return { state: 'unavailable' }; }
}

// ────────────────────────────────────────────────────────────────
// Camera (used by the QR scanner)
// ────────────────────────────────────────────────────────────────

export async function getCameraPermission() {
  if (!isNative()) {
    // On web the browser will prompt inline when the scanner requests
    // a MediaStream; there is no equivalent "check first" surface.
    return { state: 'prompt' };
  }
  return safe(async () => {
    const { BarcodeScanner } = await import('@capacitor-mlkit/barcode-scanning');
    const result = await BarcodeScanner.checkPermissions();
    return { state: result?.camera || 'prompt' };
  });
}

export async function requestCameraPermission() {
  if (!isNative()) {
    // The browser inline-prompts on getUserMedia — we surface `'prompt'`
    // here so the caller shows the "grant camera access" screen if it
    // wants to. The scanner abstraction handles the actual UX.
    return { state: 'prompt' };
  }
  return safe(async () => {
    const { BarcodeScanner } = await import('@capacitor-mlkit/barcode-scanning');
    const result = await BarcodeScanner.requestPermissions();
    return { state: result?.camera || 'denied' };
  });
}

// ────────────────────────────────────────────────────────────────
// Location (used by the charger map's "centre on me" affordance)
// ────────────────────────────────────────────────────────────────

export async function getLocationPermission() {
  if (!isNative()) {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
      return { state: 'prompt' };
    }
    return safe(async () => {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return { state: result.state || 'prompt' };
    });
  }
  return safe(async () => {
    const { Geolocation } = await import('@capacitor/geolocation');
    const result = await Geolocation.checkPermissions();
    // Geolocation returns { location, coarseLocation }; treat 'granted' /
    // 'prompt-with-rationale' as the two states worth surfacing.
    return { state: result?.location || 'prompt' };
  });
}

export async function requestLocationPermission() {
  if (!isNative()) return { state: 'prompt' };
  return safe(async () => {
    const { Geolocation } = await import('@capacitor/geolocation');
    // We only ever request FOREGROUND location on GreenCharge — never
    // background. The `permissions` array below is what iOS + Android
    // check at runtime; `location` covers both platforms' "when in use"
    // scope. Do NOT add 'coarseLocation' or a background scope here.
    const result = await Geolocation.requestPermissions({ permissions: ['location'] });
    return { state: result?.location || 'denied' };
  });
}

// ────────────────────────────────────────────────────────────────
// Notifications (push + local)
// ────────────────────────────────────────────────────────────────

export async function getNotificationPermission() {
  if (!isNative()) {
    if (typeof Notification === 'undefined') return { state: 'unavailable' };
    return { state: Notification.permission || 'prompt' };
  }
  return safe(async () => {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const result = await PushNotifications.checkPermissions();
    return { state: result?.receive || 'prompt' };
  });
}

export async function requestNotificationPermission() {
  if (!isNative()) {
    if (typeof Notification === 'undefined' || typeof Notification.requestPermission !== 'function') {
      return { state: 'unavailable' };
    }
    try {
      const r = await Notification.requestPermission();
      return { state: r || 'denied' };
    } catch { return { state: 'denied' }; }
  }
  return safe(async () => {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const result = await PushNotifications.requestPermissions();
    return { state: result?.receive || 'denied' };
  });
}
